import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {View, FlatList, StyleSheet, TouchableOpacity} from 'react-native';
import {
  Text, Appbar, Button, ActivityIndicator, Snackbar,
  Dialog, Portal, RadioButton, TextInput, IconButton,
} from 'react-native-paper';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useTaskTypeStore} from '../../store/useTaskTypeStore';
import {workerApi} from '../../services/api/workerApi';
import {orderApi} from '../../services/api/orderApi';
import DatePickerInput from '../../components/common/DatePickerInput';
import {colors, spacing} from '../../theme';

const formatDate = (d) => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const diffDays = (a, b) => {
  if (!a || !b) return 0;
  return Math.round((a - b) / (1000 * 60 * 60 * 24));
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const InstanceTaskScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const {orderId, instanceId, deliveryDate, instanceCode} = route.params;

  const {taskTypes, fetchTaskTypes, isLoading: taskTypesLoading} = useTaskTypeStore();

  const [selectedTasks, setSelectedTasks] = useState({});
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [expectedCompletionDate, setExpectedCompletionDate] = useState('');
  const [autoCalcDate, setAutoCalcDate] = useState(true);

  // Worker assignment dialog state
  const [assignDialog, setAssignDialog] = useState(null);
  const [dialogWorkerId, setDialogWorkerId] = useState(null);
  const [dialogPieceRate, setDialogPieceRate] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await fetchTaskTypes();
      const [workersRes, tasksRes] = await Promise.all([
        workerApi.list(),
        orderApi.getInstanceTasks(orderId, instanceId),
      ]);
      const wd = workersRes.data.data;
      setWorkers(wd?.content || wd || []);

      const existingTasks = tasksRes.data.data || [];
      if (existingTasks.length > 0) {
        const map = {};
        existingTasks.forEach(t => {
          map[t.taskTypeId] = {
            selected: true,
            durationDays: String(t.durationDays || 1),
            assignedWorkerId: t.assignedWorkerId,
            assignedWorkerName: t.assignedWorkerName,
            pieceRate: t.pieceRate ? String(t.pieceRate) : '',
            displayOrder: t.displayOrder,
          };
        });
        setSelectedTasks(map);
      }
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const totalDays = useMemo(() => {
    let sum = 0;
    Object.values(selectedTasks).forEach(t => {
      if (t.selected) {
        sum += parseInt(t.durationDays, 10) || 0;
      }
    });
    return sum;
  }, [selectedTasks]);

  // Auto-calculate expected completion date when total changes
  useEffect(() => {
    if (autoCalcDate && totalDays > 0) {
      const result = addDays(new Date(), totalDays);
      setExpectedCompletionDate(formatDate(result));
    }
  }, [totalDays, autoCalcDate]);

  const workStartDate = useMemo(() => {
    const ecd = parseDate(expectedCompletionDate);
    if (!ecd || totalDays === 0) return null;
    return addDays(ecd, -totalDays);
  }, [expectedCompletionDate, totalDays]);

  const daysLate = useMemo(() => {
    if (!workStartDate) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = diffDays(today, workStartDate);
    return diff > 0 ? diff : 0;
  }, [workStartDate]);

  const toggleTask = useCallback((taskTypeId, taskType) => {
    setSelectedTasks(prev => {
      const existing = prev[taskTypeId];
      if (existing?.selected) {
        return {...prev, [taskTypeId]: {...existing, selected: false}};
      }
      return {
        ...prev,
        [taskTypeId]: {
          selected: true,
          durationDays: String(taskType.durationDays || 1),
          assignedWorkerId: existing?.assignedWorkerId || null,
          assignedWorkerName: existing?.assignedWorkerName || null,
          pieceRate: existing?.pieceRate || '',
          displayOrder: existing?.displayOrder || 0,
        },
      };
    });
  }, []);

  const updateDuration = useCallback((taskTypeId, value) => {
    setSelectedTasks(prev => ({
      ...prev,
      [taskTypeId]: {...prev[taskTypeId], durationDays: value},
    }));
  }, []);

  const openAssignDialog = useCallback((taskTypeId, taskTypeName) => {
    const task = selectedTasks[taskTypeId];
    setDialogWorkerId(task?.assignedWorkerId || null);
    setDialogPieceRate(task?.pieceRate || '');
    setAssignDialog({taskTypeId, taskTypeName});
  }, [selectedTasks]);

  const confirmAssignWorker = useCallback(() => {
    if (!assignDialog) return;
    const worker = workers.find(w => w.id === dialogWorkerId);
    setSelectedTasks(prev => ({
      ...prev,
      [assignDialog.taskTypeId]: {
        ...prev[assignDialog.taskTypeId],
        assignedWorkerId: dialogWorkerId,
        assignedWorkerName: worker?.fullName || null,
        pieceRate: dialogPieceRate,
      },
    }));
    setAssignDialog(null);
  }, [assignDialog, dialogWorkerId, dialogPieceRate, workers]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const tasks = [];
      let order = 0;
      // Use taskTypes order for display order of selected tasks
      taskTypes.forEach(tt => {
        const t = selectedTasks[tt.id];
        if (t?.selected) {
          tasks.push({
            taskTypeId: tt.id,
            taskTypeName: tt.name,
            assignedWorkerId: t.assignedWorkerId || null,
            durationDays: parseInt(t.durationDays, 10) || 1,
            pieceRate: t.pieceRate ? parseFloat(t.pieceRate) : null,
            displayOrder: order++,
          });
        }
      });

      await orderApi.saveInstanceTasks(orderId, instanceId, {
        tasks,
        expectedCompletionDate: expectedCompletionDate || null,
      });
      setSnackbar('Tasks saved successfully');
      setTimeout(() => navigation.goBack(), 500);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || 'Failed to save tasks');
    } finally {
      setSaving(false);
    }
  };

  const renderTaskRow = ({item: taskType}) => {
    const task = selectedTasks[taskType.id];
    const isSelected = task?.selected;

    return (
      <TouchableOpacity
        style={[styles.taskRow, isSelected && styles.taskRowSelected]}
        onPress={() => toggleTask(taskType.id, taskType)}
        activeOpacity={0.7}>
        <IconButton
          icon={isSelected ? 'checkbox-marked-circle' : 'checkbox-blank-circle-outline'}
          size={24}
          iconColor={isSelected ? colors.primary : colors.textLight}
          style={styles.checkIcon}
        />
        <Text style={[styles.taskName, isSelected && styles.taskNameSelected]} numberOfLines={1}>
          {taskType.name}
        </Text>
        {isSelected && (
          <>
            <IconButton
              icon={task?.assignedWorkerId ? 'account-check' : 'account-plus-outline'}
              size={20}
              iconColor={task?.assignedWorkerId ? colors.success : colors.textSecondary}
              onPress={() => openAssignDialog(taskType.id, taskType.name)}
            />
            <TextInput
              value={task?.durationDays || ''}
              onChangeText={(v) => updateDuration(taskType.id, v)}
              keyboardType="numeric"
              style={styles.durationInput}
              mode="outlined"
              dense
              outlineColor={colors.border}
              activeOutlineColor={colors.primary}
            />
            <Text style={styles.daysLabel}>Days</Text>
          </>
        )}
      </TouchableOpacity>
    );
  };

  if (loading || taskTypesLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title="Select Tasks" titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <View style={styles.heading}>
        <Text variant="titleMedium" style={styles.headingText}>
          Manage Tasks for {instanceCode}
        </Text>
      </View>

      <FlatList
        data={taskTypes}
        keyExtractor={item => String(item.id)}
        renderItem={renderTaskRow}
        style={styles.list}
        contentContainerStyle={styles.listContent}
      />

      {/* Bottom Summary Section */}
      <View style={styles.bottomSection}>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Total Duration</Text>
          <Text style={styles.summaryValue}>{totalDays} Days</Text>
        </View>

        {deliveryDate && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Delivery Date</Text>
            <Text style={styles.summaryValue}>{deliveryDate}</Text>
          </View>
        )}

        <DatePickerInput
          label="Expected Completion Date"
          value={expectedCompletionDate}
          onChangeText={(v) => {
            setExpectedCompletionDate(v);
            setAutoCalcDate(false);
          }}
          style={styles.dateInput}
        />

        {workStartDate && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Work Start Date</Text>
            <Text style={styles.summaryValue}>{formatDate(workStartDate)}</Text>
          </View>
        )}

        {daysLate > 0 && (
          <Text style={styles.lateWarning}>
            You are {daysLate} day{daysLate > 1 ? 's' : ''} late!
          </Text>
        )}

        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving}
          style={styles.saveButton}
          buttonColor={colors.primary}>
          Save Tasks
        </Button>
      </View>

      {/* Worker Assignment Dialog */}
      <Portal>
        <Dialog
          visible={!!assignDialog}
          onDismiss={() => setAssignDialog(null)}
          style={styles.dialog}>
          <Dialog.Title>
            Assign "{assignDialog?.taskTypeName}" to
          </Dialog.Title>
          <Dialog.ScrollArea style={styles.dialogScroll}>
            <RadioButton.Group
              value={dialogWorkerId ? String(dialogWorkerId) : ''}
              onValueChange={v => setDialogWorkerId(v ? Number(v) : null)}>
              {workers.map(w => (
                <View key={w.id}>
                  <RadioButton.Item
                    label={`${w.fullName}${w.roleName ? ` (${w.roleName})` : ''}`}
                    value={String(w.id)}
                    labelStyle={styles.workerLabel}
                  />
                  {dialogWorkerId === w.id && (
                    <TextInput
                      label="Piece Rate"
                      value={dialogPieceRate}
                      onChangeText={setDialogPieceRate}
                      keyboardType="numeric"
                      mode="outlined"
                      dense
                      style={styles.pieceRateInput}
                      outlineColor={colors.border}
                      activeOutlineColor={colors.primary}
                    />
                  )}
                </View>
              ))}
            </RadioButton.Group>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={() => setAssignDialog(null)}>Cancel</Button>
            <Button onPress={confirmAssignWorker}>Assign Worker</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  heading: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headingText: {fontWeight: '600', color: colors.text},
  list: {flex: 1},
  listContent: {paddingBottom: spacing.sm},
  taskRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.xs,
    paddingRight: spacing.md,
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  taskRowSelected: {backgroundColor: colors.primary + '08'},
  checkIcon: {margin: 0},
  taskName: {flex: 1, color: colors.textSecondary, fontSize: 14},
  taskNameSelected: {color: colors.text, fontWeight: '500'},
  durationInput: {
    width: 56,
    height: 36,
    backgroundColor: colors.surface,
    fontSize: 14,
    textAlign: 'center',
  },
  daysLabel: {
    color: colors.textSecondary,
    fontSize: 12,
    marginLeft: spacing.xs,
    width: 30,
  },
  bottomSection: {
    backgroundColor: colors.surface,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  summaryLabel: {color: colors.textSecondary, fontSize: 14},
  summaryValue: {color: colors.text, fontWeight: '600', fontSize: 14},
  dateInput: {marginVertical: spacing.xs},
  lateWarning: {
    color: colors.error,
    fontWeight: 'bold',
    textAlign: 'center',
    marginVertical: spacing.xs,
    fontSize: 14,
  },
  saveButton: {marginTop: spacing.sm},
  dialog: {maxHeight: '80%'},
  dialogScroll: {maxHeight: 400, paddingHorizontal: 0},
  workerLabel: {fontSize: 14},
  pieceRateInput: {
    marginHorizontal: spacing.lg,
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
});

export default InstanceTaskScreen;
